import React from 'react';

import { Button, Divider, Modal, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { ReactComponent as CancelIcon } from 'src/common/assets/icons/cancel_icon_gray.svg';
import ExportPDFButton from 'src/common/components/ExportPDFButton';
import { SelectableCode } from 'src/common/types/Codes';
import { SaveSheet } from 'src/common/types/SaveSheet';
import EditTextButton from 'src/florence/components/SearchResultsContainerV2/componentsV2/EditTextButton';
import NoteText from 'src/florence/components/SearchResultsContainerV2/componentsV2/NoteText';

type Props = {
  note: SaveSheet | null | undefined;
  refreshResults: (text: string) => void;
  onSaveNote: () => void;
  isLoading: boolean;
  saveIsLoading: boolean;
  selectedCodes: SelectableCode[];
  clearSelectedCodes: () => void;
  selectCode: (code: SelectableCode) => void;
  isFinalizing: boolean;
  onFinalize: () => void;
};

const SurgeryEngineHeader: React.FC<Props> = ({
  note,
  refreshResults,
  onSaveNote,
  isLoading,
  saveIsLoading,
  selectedCodes,
  clearSelectedCodes,
  selectCode,
  isFinalizing,
  onFinalize,
}) => {
  const [popup, setPopup] = React.useState<null | 'view' | 'edit'>(null);
  const [editedSearchText, setEditedSearchText] = React.useState(
    note?.scanText ?? ''
  );

  React.useEffect(() => {
    setEditedSearchText(note?.scanText ?? '');
  }, [note?.scanText]);

  const closePopup = () => {
    setPopup(null);
    setEditedSearchText(note?.scanText ?? '');
  };

  const confirmChanges = () => {
    if (editedSearchText.trim().length > 0) {
      refreshResults(editedSearchText.trim());
      setPopup(null);
    }
  };

  return (
    <Box
      id='op-note-header'
      sx={{
        width: '100%',
        backgroundColor: '#81B5CA',
        borderRadius: '16px 16px 0px 0px',
      }}
    >
      <Box
        sx={{
          gap: '24px',
          display: 'flex',
          flexWrap: {
            xs: 'wrap',
            md: 'nowrap',
          },
          alignItems: 'center',
          borderBottom: '1px solid rgba(82, 86, 98, 0.20)',
          padding: {
            xs: '24px 16px',
            lg: '24px 60px',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: 'rgba(0, 33, 69, 0.50)',
              marginRight: '20px',
              paddingTop: '4px',
              lineHeight: '20px',
            }}
          >
            NOTE
          </Typography>
          <NoteText onViewMore={() => setPopup('view')} lineCount={5}>
            {note?.scanText}
          </NoteText>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '8px',
          }}
        >
          <EditTextButton
            isDisabled={isLoading}
            onClick={() => setPopup('edit')}
          />
          {note && (
            <ExportPDFButton
              isDisabled={isLoading}
              note={note}
              selectedCodes={selectedCodes}
              small
            />
          )}
        </Box>
      </Box>

      <Modal open={!!popup} onClose={() => closePopup()}>
        <Box
          sx={{
            position: 'absolute' as const,
            width: '100%',
            height: '100%',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: '100%',
              backgroundColor: 'appMerino.main',
              padding: {
                xs: '20px 20px 20px 60px',
                md: '20px 40px 40px 60px',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '90px',
                width: '100%',
              }}
            >
              <Button
                onClick={() => closePopup()}
                variant='text'
                startIcon={<CancelIcon />}
              >
                <Typography color='text.secondary'>
                  {popup === 'view' ? 'Back' : 'Cancel'}
                </Typography>
              </Button>
              {popup === 'edit' && (
                <Button
                  onClick={confirmChanges}
                  variant='contained'
                  color='darkBlue'
                  disableElevation
                  sx={{
                    width: 'auto',
                    height: 48,
                    borderRadius: '15px',
                  }}
                >
                  <Typography color='white'>Confirm Changes</Typography>
                </Button>
              )}
            </Box>
            <Divider sx={{ marginTop: '16px' }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                backgroundColor: 'appMerino.main',
                marginTop: '20px',
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: 'rgba(0, 33, 69, 0.50)',
                  marginBottom: '8px',
                }}
              >
                {popup === 'view' ? 'VIEW NOTE' : 'EDIT NOTE'}
              </Typography>
              <TextField
                onChange={(e) => setEditedSearchText(e.target.value ?? '')}
                variant='standard'
                multiline
                fullWidth
                value={editedSearchText}
                sx={{
                  mb: '30px',
                }}
                InputProps={{ disableUnderline: true, spellCheck: false }}
                inputProps={{ style: { color: '#002145B2' } }}
              />
              <Divider />
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default SurgeryEngineHeader;
